// Type-only import: erased at compile, so the SDK's Node-only package is never pulled into the
// browser bundle. Lets the annotation reference the SDK enum while the value stays a numeric constant.
import type { LogLevel } from '@amplitude/experiment-node-server';

export const ANON_ID_KEY = 'ajs_anonymous_id';

export interface IVariantResult<TPayload = unknown> {
  key?: string;
  payload?: TPayload;
}

/** Shape of the client instance provided via AMPLITUDE_EXPERIMENT_CLIENT (avoids importing Node-only package). */
export interface IExperimentClientInstance {
  fetchV2: (
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Amplitude API contract
    user: { device_id: string; },
    opts: { flagKeys: string[]; }
  ) => Promise<Record<string, IVariantResult<unknown>>>;
}

/** Constructor options for the experiment client (matches @amplitude/experiment-node-server). */
export interface IExperimentClientOptions {
  serverUrl: string;
  fetchTimeoutMillis: number;
  fetchRetries: number;
  fetchRetryBackoffMinMillis: number;
  fetchRetryTimeoutMillis: number;
  /** SDK LogLevel (Disable..Verbose). Type-only reference to the SDK enum; the value stays a numeric constant. */
  logLevel?: LogLevel;
}

/** Constructor type for the client provided via AMPLITUDE_EXPERIMENT_CLIENT. */
export type ExperimentClientConstructor = new (
  deploymentKey: string,
  options: IExperimentClientOptions
) => IExperimentClientInstance;

/** Minimal response shape for setting a cookie/header during SSR (avoids importing express). */
export interface IResponse {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- express Response API
  cookie?: (name: string, value: string, options?: Record<string, unknown>) => void;
  setHeader?: (name: string, value: string) => void;
  headersSent?: boolean;
}

/** One year, matching Segment's default ajs_anonymous_id cookie lifetime. */
export const ANON_ID_COOKIE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

/** Amplitude Experiment API base URL (Sondermind proxy). */
export const AMPLITUDE_SERVER_URL = 'https://amplitude.sondermind.com';

/** Timeout for each fetch to Amplitude. Kept low so SSR render does not block too long on flag evaluation. */
export const FETCH_TIMEOUT_MILLIS = 1500;
/** Number of retries after a failed fetch. Balances reliability with keeping SSR response time bounded. */
export const FETCH_RETRIES = 2;
/** No delay before first retry; subsequent retries use exponential backoff. */
export const FETCH_RETRY_BACKOFF_MIN_MILLIS = 0;
/** Max time allowed for the entire retry sequence. */
export const FETCH_RETRY_TIMEOUT_MILLIS = 1500;
/**
 * SDK LogLevel.Disable. Silences the SDK's internal AmplitudeLogger, whose fetch/retry/socket-timeout
 * lines (default LogLevel.Error) are benign on SSR — the app catches the rejection and serves the
 * default variant — but flood provider-directory-ssr with noise-only status:error logs.
 */
export const EXPERIMENT_LOG_LEVEL_DISABLE = 0;
